import { isDefined } from "@clipboard-health/util-ts";
import { isValid, parseISO, startOfDay } from "date-fns";

import { usePaginatedWorkerShifts } from "../../WorkerShifts/api/usePaginatedWorkerShifts";
import { useWorkerPendingShiftInvites } from "../../WorkerShifts/ShiftInvites/api/useWorkerPendingShiftInvites";

interface UseGetPendingJobFairInviteAndShiftBookedDataParams {
  workerId: string;
  jobFairHcfId: string;
  jobFairTimestamp: string;
  enabled?: boolean;
}

/**
 * Hook to check if a worker has a shift booked at the job fair HCF on the job fair date.
 *
 * @param params - Configuration object
 * @param params.workerId - The ID of the worker
 * @param params.jobFairHcfId - The ID of the job fair HCF
 * @param params.jobFairTimestamp - ISO timestamp of the job fair
 * @param params.enabled - Whether the hook should be enabled (defaults to true)
 *
 * @returns Object containing:
 * - hasJobFairShift: boolean indicating if the worker has a shift booked at the job fair HCF on the job fair date
 * - hasPendingJobFairInvite: boolean indicating if the worker has a pending shift invite for the job fair HCF
 * - isLoading: boolean indicating if the check is in progress
 */
export function useGetPendingJobFairInviteAndShiftBookedData(
  params: UseGetPendingJobFairInviteAndShiftBookedDataParams
) {
  const { workerId, jobFairHcfId, jobFairTimestamp, enabled = true } = params;

  const jobFairDate = parseISO(jobFairTimestamp);
  const isValidDate = isValid(jobFairDate);
  const dayStart = isValidDate ? startOfDay(jobFairDate) : undefined;

  const {
    data: workerShiftsInviteData,
    isLoading: isLoadingPendingShiftInvites,
    fetchStatus: fetchStatusPendingShiftInvites,
  } = useWorkerPendingShiftInvites({
    filterParams: {
      shiftDetails: {
        start: {
          gte: dayStart?.toISOString(),
        },
      },
    },
    options: {
      enabled: enabled && isDefined(workerId) && isDefined(jobFairHcfId) && isValidDate,
    },
  });

  const {
    data: workerShiftsData,
    isLoading: isLoadingWorkerShifts,
    fetchStatus: fetchStatusWorkerShifts,
  } = usePaginatedWorkerShifts(
    {
      workerId,
      workplaceId: jobFairHcfId,
      filter: {
        start: {
          gt: dayStart,
        },
        deleted: false,
      },
    },
    {
      enabled: enabled && isDefined(workerId) && isDefined(jobFairHcfId) && isValidDate,
    }
  );

  const hasPendingJobFairInvite =
    isDefined(workerShiftsInviteData?.data) &&
    workerShiftsInviteData.data.some((shiftInvite) => {
      return shiftInvite.attributes.workplaceId === jobFairHcfId;
    });

  const hasJobFairShift =
    !hasPendingJobFairInvite &&
    (workerShiftsData?.pages.flatMap((page) => page.data) ?? []).length > 0;

  return {
    hasPendingJobFairInvite,
    hasJobFairShift,
    isLoading:
      (isLoadingPendingShiftInvites || isLoadingWorkerShifts) &&
      (fetchStatusPendingShiftInvites !== "idle" || fetchStatusWorkerShifts !== "idle"),
  };
}
